import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { api } from "../lib/api";
import { Lock, ArrowLeft, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useLang();
  const navigate = useLocaleNavigate();
  const { applyAuthResponse } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const tokenMissing = !token || token.length < 20;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) { setError(`${t.auth.password}: min 6`); return; }
    if (pwd !== confirmPwd) { setError(t.auth.resetMismatch); return; }
    setBusy(true);
    try {
      const res = await api.resetPassword(token, pwd);
      // Persist token + user via AuthContext so the user is logged in immediately
      if (applyAuthResponse) applyAuthResponse(res);
      toast.success(t.auth.resetSuccess);
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Error";
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md items-center px-4">
      <Card className="w-full border-zinc-800 bg-zinc-950 p-8 text-white" data-testid="reset-password-card">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">{t.nav.login}</p>
        <h1 className="mt-2 font-display text-3xl font-bold">{t.auth.resetTitle}</h1>

        {tokenMissing ? (
          <div className="mt-6 space-y-5" data-testid="reset-token-missing">
            <div className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
              <p className="text-sm leading-relaxed text-zinc-200">{t.auth.resetMissingToken}</p>
            </div>
            <Link to="/forgot-password" className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-500" data-testid="reset-request-new">
              {t.auth.resetTryAgain}
            </Link>
          </div>
        ) : (
          <>
            <p className="mt-4 text-sm leading-relaxed text-zinc-400">{t.auth.resetIntro}</p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.auth.resetNew}</Label>
                <div className="relative mt-2">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <Input
                    type="password"
                    required
                    minLength={6}
                    autoComplete="new-password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    className="border-zinc-800 bg-black pl-10 text-white"
                    data-testid="reset-password-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">{t.auth.resetConfirm}</Label>
                <div className="relative mt-2">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                  <Input
                    type="password"
                    required
                    minLength={6}
                    autoComplete="new-password"
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    className="border-zinc-800 bg-black pl-10 text-white"
                    data-testid="reset-password-confirm-input"
                  />
                </div>
              </div>
              {error && <p className="text-sm text-red-500" data-testid="reset-error">{error}</p>}
              <Button type="submit" disabled={busy || !pwd || !confirmPwd} className="w-full bg-red-600 text-white hover:bg-red-500" data-testid="reset-submit">
                {busy ? "..." : t.auth.resetSubmit}
              </Button>
            </form>
          </>
        )}

        <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-red-500" data-testid="reset-back-to-login">
          <ArrowLeft className="h-3.5 w-3.5" /> {t.auth.forgotBack}
        </Link>
      </Card>
    </div>
  );
}
